import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Photo from "../components/ui/Photo";
import { SALON, staffById, staffFor } from "../data/salon";
import { duration, toFa, toman } from "../lib/format";
import { useBooking } from "../lib/booking";
import PageHero from "../components/ui/PageHero";
import { useDocumentTitle } from "../lib/title";
import "./pages.css";

const STEPS = [
  { n: 1, label: "خدمات" },
  { n: 2, label: "متخصص" },
  { n: 3, label: "زمان" },
  { n: 4, label: "تأیید" },
];

const SLOTS = ["۹:۰۰", "۱۰:۳۰", "۱۲:۰۰", "۱۳:۳۰", "۱۵:۰۰", "۱۶:۳۰", "۱۸:۰۰", "۱۹:۳۰"];

/** Next 14 days, formatted on the Persian calendar. Fridays are closed. */
function useDays() {
  return useMemo(() => {
    const fmtDay = new Intl.DateTimeFormat("fa-IR", { day: "numeric" });
    const fmtMonth = new Intl.DateTimeFormat("fa-IR", { month: "long" });
    const fmtWeek = new Intl.DateTimeFormat("fa-IR", { weekday: "long" });

    return Array.from({ length: 14 }, (_, i) => {
      const d = new Date();
      d.setDate(d.getDate() + i + 1);
      return {
        iso: d.toISOString().slice(0, 10),
        day: fmtDay.format(d),
        month: fmtMonth.format(d),
        week: fmtWeek.format(d),
        closed: d.getDay() === 5, // Friday
      };
    });
  }, []);
}

export default function Booking() {
  useDocumentTitle("رزرو نوبت");
  const b = useBooking();
  const days = useDays();

  const [step, setStep] = useState(1);
  const [day, setDay] = useState(null);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", note: "" });
  const [done, setDone] = useState(false);

  const canNext =
    (step === 1 && b.count > 0) ||
    step === 2 ||
    (step === 3 && day && slot) ||
    step === 4;

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) return;
    setDone(true);
    b.clear();
  };

  /* ---------------------------------------------------------- success */
  if (done) {
    const chosen = days.find((d) => d.iso === day);
    return (
      <section className="section bconfirm">
        <div className="wrap wrap--narrow bconfirm__inner">
          <span className="bconfirm__tick" aria-hidden="true">✓</span>
          <span className="eyebrow">CONFIRMED</span>
          <h1>نوبتت ثبت شد</h1>
          <p>
            {form.name} عزیز، نوبت شما برای{" "}
            <strong>{chosen?.week} {chosen?.day} {chosen?.month}</strong> ساعت{" "}
            <strong>{slot}</strong> ثبت شد. تا دقایقی دیگر پیامک تأیید برایت
            ارسال می‌شود.
          </p>
          <p className="bconfirm__note">
            برای تغییر یا لغو، تا ۲۴ ساعت قبل با {SALON.phone} تماس بگیر.
          </p>
          <Link to="/" className="btn">بازگشت به خانه</Link>
        </div>
      </section>
    );
  }

  return (
    <>
      <PageHero
        eyebrow="BOOK AN APPOINTMENT"
        title="رزرو نوبت"
        lede="چهار قدم کوتاه؛ تأیید نهایی با پیامک برایت ارسال می‌شود."
        tone="#E6D2C3"
      />

      <section className="section bk">
        <div className="wrap wrap--wide bk__grid">
          <div className="bk__main">
            {/* -------------------------------------------------- stepper */}
            <ol className="stepper">
              {STEPS.map((s) => (
                <li
                  key={s.n}
                  className={`stepper__i${step === s.n ? " is-on" : ""}${step > s.n ? " is-done" : ""}`}
                >
                  <button type="button" onClick={() => s.n < step && setStep(s.n)}>
                    <span className="stepper__n">{step > s.n ? "✓" : toFa(s.n)}</span>
                    <span className="stepper__l">{s.label}</span>
                  </button>
                </li>
              ))}
            </ol>

            {/* ------------------------------------------------- 1 services */}
            {step === 1 && (
              <div className="bstep">
                <h2 className="bstep__title">خدمات انتخاب‌شده</h2>

                {b.count === 0 ? (
                  <div className="empty">
                    <h3>سبد نوبت خالی است</h3>
                    <p>اول خدمت مورد نظرت را انتخاب کن.</p>
                    <Link to="/services" className="btn btn--sm" style={{ marginTop: "var(--s-4)" }}>
                      دیدن خدمات
                    </Link>
                  </div>
                ) : (
                  <ul className="bk__list">
                    {b.items.map((i) => (
                      <li key={i.key} className="bk__row" style={{ "--tone": i.tone }}>
                        <Photo src={i.photo} alt="" ratio="1 / 1" className="bk__art" />
                        <span className="bk__rowMain">
                          <strong>{i.name}</strong>
                          <span>{duration(i.duration)}</span>
                        </span>
                        <span className="bk__rowPrice">{toman(i.price)}</span>
                        <button type="button" onClick={() => b.remove(i.key)} aria-label={`حذف ${i.name}`}>
                          حذف
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* --------------------------------------------------- 2 staff */}
            {step === 2 && (
              <div className="bstep">
                <h2 className="bstep__title">متخصص هر خدمت</h2>
                <p className="bstep__lede">
                  اگر ترجیحی نداری، خالی بگذار؛ نزدیک‌ترین متخصص آزاد را اختصاص می‌دهیم.
                </p>

                <div className="bk__staff">
                  {b.items.map((i) => {
                    const team = staffFor(i.category);
                    return (
                      <div key={i.key} className="bk__pick">
                        <span className="bk__pickName">{i.name}</span>
                        <div className="sdetail__chips">
                          <button
                            type="button"
                            className={`schip${i.staffId === null ? " is-on" : ""}`}
                            onClick={() => b.setStaff(i.key, null)}
                          >
                            فرقی ندارد
                          </button>
                          {team.map((m) => (
                            <button
                              key={m.id}
                              type="button"
                              className={`schip${i.staffId === m.id ? " is-on" : ""}`}
                              style={{ "--chip": m.tone }}
                              onClick={() => b.setStaff(i.key, m.id)}
                            >
                              <span className="schip__dot" aria-hidden="true" />
                              {m.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ---------------------------------------------------- 3 time */}
            {step === 3 && (
              <div className="bstep">
                <h2 className="bstep__title">روز و ساعت</h2>

                <div className="bk__days">
                  {days.map((d) => (
                    <button
                      key={d.iso}
                      type="button"
                      disabled={d.closed}
                      className={`bday${day === d.iso ? " is-on" : ""}`}
                      onClick={() => setDay(d.iso)}
                    >
                      <span className="bday__w">{d.week}</span>
                      <span className="bday__d">{d.day}</span>
                      <span className="bday__m">{d.closed ? "تعطیل" : d.month}</span>
                    </button>
                  ))}
                </div>

                <h3 className="bk__sub">ساعت</h3>
                <div className="bk__slots">
                  {SLOTS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      disabled={!day}
                      className={`bslot${slot === s ? " is-on" : ""}`}
                      onClick={() => setSlot(s)}
                    >
                      {s}
                    </button>
                  ))}
                </div>
                {!day && <p className="bk__hint">اول یک روز انتخاب کن.</p>}
              </div>
            )}

            {/* -------------------------------------------------- 4 detail */}
            {step === 4 && (
              <form className="bstep" onSubmit={submit}>
                <h2 className="bstep__title">مشخصات تماس</h2>

                <div className="bk__fields">
                  <label>
                    <span>نام و نام خانوادگی</span>
                    <input
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="مثلاً نگار محمدی"
                    />
                  </label>

                  <label>
                    <span>شماره موبایل</span>
                    <input
                      required
                      inputMode="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="۰۹۱۲۰۰۰۰۰۰۰"
                    />
                  </label>

                  <label className="bk__full">
                    <span>توضیح (اختیاری)</span>
                    <textarea
                      rows={3}
                      value={form.note}
                      onChange={(e) => setForm({ ...form, note: e.target.value })}
                      placeholder="حساسیت، ترجیح رنگ، یا هر نکته‌ای که باید بدانیم"
                    />
                  </label>
                </div>

                <button type="submit" className="btn btn--block">ثبت نهایی نوبت</button>
              </form>
            )}

            {/* --------------------------------------------------- controls */}
            {step < 4 && (
              <div className="bk__nav">
                {step > 1 && (
                  <button type="button" className="btn btn--ghost" onClick={() => setStep(step - 1)}>
                    مرحله قبل
                  </button>
                )}
                <button
                  type="button"
                  className="btn"
                  disabled={!canNext}
                  onClick={() => setStep(step + 1)}
                >
                  ادامه
                </button>
              </div>
            )}
          </div>

          {/* ------------------------------------------------------ summary */}
          <aside className="bk__aside">
            <div className="bsum">
              <h2>خلاصه نوبت</h2>

              {b.count === 0 ? (
                <p className="bsum__empty">هنوز خدمتی انتخاب نشده.</p>
              ) : (
                <ul className="bsum__list">
                  {b.items.map((i) => (
                    <li key={i.key}>
                      <span>{i.name}</span>
                      <span>{toman(i.price)}</span>
                    </li>
                  ))}
                </ul>
              )}

              <dl className="bsum__facts">
                <div>
                  <dt>مدت کل</dt>
                  <dd>{b.minutes ? duration(b.minutes) : "—"}</dd>
                </div>
                <div>
                  <dt>روز</dt>
                  <dd>
                    {day
                      ? (() => {
                          const d = days.find((x) => x.iso === day);
                          return `${d.week} ${d.day} ${d.month}`;
                        })()
                      : "—"}
                  </dd>
                </div>
                <div>
                  <dt>ساعت</dt>
                  <dd>{slot || "—"}</dd>
                </div>
                <div>
                  <dt>متخصص</dt>
                  <dd>
                    {b.items.some((i) => i.staffId)
                      ? b.items
                          .filter((i) => i.staffId)
                          .map((i) => staffById(i.staffId)?.name)
                          .join("، ")
                      : "بدون ترجیح"}
                  </dd>
                </div>
              </dl>

              <div className="bsum__total">
                <span>جمع</span>
                <strong>{toman(b.total)}</strong>
              </div>

              <p className="bsum__note">
                پرداخت در محل انجام می‌شود. لغو تا ۲۴ ساعت قبل رایگان است.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
